import { Box, Chip, Divider, IconButton, Skeleton, Typography, useTheme } from '@mui/material';
import { useReducer } from 'react';
import { Link } from 'react-router-dom';
import { Utilities } from 'utilities/utilities';
import accountNameIcon from '../../../../../../assets/account-name.svg';
import birthDateIcon from '../../../../../../assets/birthdate.svg';
import companyIcon from '../../../../../../assets/building.svg';
import deleteIcon from '../../../../../../assets/delete-red.png';
import editIcon from '../../../../../../assets/edit-thin.svg';
import emailIcon from '../../../../../../assets/email.svg';
import linkIcon from '../../../../../../assets/link-2.svg';
import mailDarkIcon from '../../../../../../assets/mail-dark.svg';
import mobileDarkIcon from '../../../../../../assets/mobile-dark.svg';
import mobileIcon from '../../../../../../assets/mobile.svg';
import relationshipIcon from '../../../../../../assets/relationship.svg';
import { CompleteOperationType, StartOperationType } from '../../../../../../model/alerts.model';
import { ToggleDrawer } from '../../../../../../model/contacts.model';
import { modalReducer } from '../../../../../../reducers/modalReducer';
import { HuddleClientService } from '../../../../../../service/huddle-client.service';
import zeplinColor from '../../../../../../theme/Color';
import CopyableText from '../../../../CopyableText/CopyableText';
import CustomCard from '../../../../CustomCard/CustomCard';
import CustomDeleteModal from '../../../../CustomDeleteModal/CustomDeleteModal';
import styles from './ContactDetail.module.css';

export const ContactDetail = ({
  selected,
  loading,
  isHuddleClient,
  handleEditDrawer,
  fetchContacts,
  startOperation,
  completeOperation,
}: {
  selected: any;
  loading?: boolean;
  isHuddleClient?: boolean;
  handleEditDrawer?: ToggleDrawer;
  fetchContacts?: (id: string) => Promise<void>;
  startOperation?: StartOperationType;
  completeOperation?: CompleteOperationType;
}) => {
  const theme = useTheme();

  const [modalState, modalDispatch] = useReducer(modalReducer, {
    delete: false,
  });

  const handleDeleteContact = async () => {
    modalDispatch({ type: 'CLOSE', payload: 'delete' });
    startOperation?.('delete', 'Deleting contact...');
    try {
      await HuddleClientService.deleteClientContact(selected.contactId);
      await fetchContacts?.(selected.accountId);
      completeOperation?.('delete', 'Deletion completed');
    } catch (error) {
      completeOperation?.('delete', 'Failed to delete file.', true);
    }
  };

  if (loading) {
    return (
      <CustomCard
        styles={{
          height: '100%',
          backgroundColor: zeplinColor.Neutral1,
          borderWidth: `1px 1px 1px 1px`,
          borderStyle: 'solid',
          borderColor: `${zeplinColor.Neutral5}`,
          borderRadius: '14px 14px 0px 0px',
          overflowY: 'scroll',
        }}
      >
        <Box
          sx={{
            padding: { xs: '20px', xl: '24px' },
            display: 'flex',
            gap: '12px',
            alignItems: 'center',
            justifyContent: 'space-between',
            position: 'sticky',
            backgroundColor: zeplinColor.Neutral1,
            top: 0,
            height: { xs: '84px', xl: '104px' },
          }}
        >
          <Skeleton variant='rounded' width={68} height={68} />
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              flexDirection: 'column',
              gap: '8px',
              flex: 1,
            }}
          >
            <Skeleton
              variant='text'
              sx={{
                fontSize: theme.typography.body2,
                flex: 1,
              }}
            />
          </Box>
        </Box>
        <Divider
          component='div'
          sx={{
            borderColor: zeplinColor.Neutral5,
            position: 'sticky',
            top: { xs: '84px', xl: '104px' },
          }}
        />
        <Box sx={{ padding: { xs: '20px', xl: '24px' } }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: { xs: '20px', xl: '24px' } }}>
            <Box sx={{ display: 'flex', gap: '48px' }}>
              <Box sx={{ display: 'flex', gap: '8px', flex: 1, alignItems: 'center' }}>
                <img
                  src={accountNameIcon}
                  className={styles.icon}
                  style={{ objectFit: 'fill' }}
                  alt='account name icon'
                />
                <Typography variant='responsiveBody2' color={zeplinColor.Neutral8} sx={{ flex: 1 }}>
                  Account name
                </Typography>
                <Skeleton
                  variant='text'
                  sx={{
                    fontSize: theme.typography.body2,
                    flex: 1,
                  }}
                />
              </Box>
            </Box>
            <Box sx={{ display: 'flex', gap: '48px' }}>
              <Box sx={{ display: 'flex', gap: '8px', flex: 1, alignItems: 'center' }}>
                <img
                  src={companyIcon}
                  className={styles.icon}
                  style={{ objectFit: 'fill' }}
                  alt='building icon'
                />
                <Typography variant='responsiveBody2' color={zeplinColor.Neutral8} sx={{ flex: 1 }}>
                  Company Name
                </Typography>
                <Skeleton
                  variant='text'
                  sx={{
                    fontSize: theme.typography.body2,
                    flex: 3,
                  }}
                />
              </Box>
            </Box>
            <Box sx={{ display: 'flex', gap: '48px' }}>
              <Box sx={{ display: 'flex', gap: '8px', flex: 1, alignItems: 'center' }}>
                <img
                  src={mobileIcon}
                  className={styles.icon}
                  style={{ objectFit: 'fill' }}
                  alt='mobile icon'
                />
                <Typography variant='responsiveBody2' color={zeplinColor.Neutral8} sx={{ flex: 1 }}>
                  Mobile
                </Typography>
                <Skeleton
                  variant='text'
                  sx={{
                    fontSize: theme.typography.body2,
                    flex: 3,
                  }}
                />
              </Box>
            </Box>
            <Box sx={{ display: 'flex', gap: '48px' }}>
              <Box sx={{ display: 'flex', gap: '8px', flex: 1, alignItems: 'center' }}>
                <img
                  src={emailIcon}
                  className={styles.icon}
                  style={{ objectFit: 'fill' }}
                  alt='email icon'
                />
                <Typography variant='responsiveBody2' color={zeplinColor.Neutral8} sx={{ flex: 1 }}>
                  Email
                </Typography>
                <Skeleton
                  variant='text'
                  sx={{
                    fontSize: theme.typography.body2,
                    flex: 3,
                  }}
                />
              </Box>
            </Box>
            <Box sx={{ display: 'flex', gap: '48px' }}>
              <Box sx={{ display: 'flex', gap: '8px', flex: 1, alignItems: 'center' }}>
                <img
                  src={relationshipIcon}
                  className={styles.icon}
                  style={{ objectFit: 'fill' }}
                  alt='relationship icon'
                />
                <Typography variant='responsiveBody2' color={zeplinColor.Neutral8} sx={{ flex: 1 }}>
                  Relationship
                </Typography>

                <Skeleton
                  variant='text'
                  sx={{
                    fontSize: theme.typography.body2,
                    flex: 3,
                  }}
                />
              </Box>
            </Box>
            <Box sx={{ display: 'flex', gap: '48px' }}>
              <Box sx={{ display: 'flex', gap: '8px', flex: 1, alignItems: 'center' }}>
                <img
                  src={birthDateIcon}
                  className={styles.icon}
                  style={{ objectFit: 'fill' }}
                  alt='birthdate icon'
                />
                <Typography variant='responsiveBody2' color={zeplinColor.Neutral8} sx={{ flex: 1 }}>
                  Birthdate
                </Typography>

                <Skeleton
                  variant='text'
                  sx={{
                    fontSize: theme.typography.body2,
                    flex: 3,
                  }}
                />
              </Box>
            </Box>
          </Box>
        </Box>
      </CustomCard>
    );
  }

  return (
    <CustomCard
      styles={{
        height: '100%',
        backgroundColor: zeplinColor.Neutral1,
        borderWidth: `1px 1px 1px 1px`,
        borderStyle: 'solid',
        borderColor: `${zeplinColor.Neutral5}`,
        borderRadius: '14px 14px 0px 0px',
        overflowY: 'scroll',
      }}
    >
      <Box
        sx={{
          padding: { xs: '20px', xl: '24px' },
          display: 'flex',
          gap: '12px',
          alignItems: 'center',
          justifyContent: 'space-between',
          position: 'sticky',
          backgroundColor: zeplinColor.Neutral1,
          top: 0,
          height: { xs: '84px', xl: '104px' },
        }}
      >
        <Box
          sx={{
            borderRadius: '14px',
            width: { xs: '58px', xl: '68px' },
            height: { xs: '58px', xl: '68px' },
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            gap: '10px',
          }}
        >
          <Box
            sx={{
              borderRadius: '14px',
              width: { xs: '58px', xl: '68px' },
              height: { xs: '58px', xl: '68px' },
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              gap: '10px',
              backgroundColor: zeplinColor.Neutral5,
              overflow: 'hidden',
            }}
          >
            {!selected.profilePicture ? (
              <Typography variant='responsiveHeading2' color={zeplinColor.Neutral6}>
                {selected.name
                  .split(' ')
                  .map((n: string) => n[0])
                  .join('')
                  .toUpperCase()}
              </Typography>
            ) : (
              <img src={selected.profilePicture} alt='contact' className={styles.pfp} />
            )}
          </Box>
        </Box>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            flexDirection: 'column',
            gap: '8px',
            flex: 1,
          }}
        >
          <Typography variant='responsiveDisplay5' style={{ textAlign: 'left' }}>
            {selected?.name}
          </Typography>
        </Box>
        {!isHuddleClient && (
          <Link
            to={`https://aamanagementgroup--aampreprod.sandbox.my.salesforce.com/'lightning/r/Account/${selected?.AccountId}/related/AccountContactRelations/view`}
            style={{ color: zeplinColor.BasicBlack, textDecoration: 'none' }}
          >
            <CustomCard
              styles={{
                borderRadius: '50px',
                padding: '10px 10px 10px 16px',
                backgroundColor: zeplinColor.Neutral1,
                border: `1px solid ${zeplinColor.Neutral6}`,
                display: 'flex',
                gap: '8px',
                // flexDirection: 'column',
                alignItems: 'flex-end',
                justifyContent: 'space-between',
                cursor: 'pointer',
              }}
            >
              <Typography variant='body3'>Salesforce</Typography>
              <img src={linkIcon} width={20} height={20} alt='arrow icon' />
            </CustomCard>
          </Link>
        )}
        <Box>
          <IconButton sx={{ width: '32px', height: '32px' }}>
            <img src={mailDarkIcon} alt='mail icon' width={'20px'} height={'20px'} />
          </IconButton>
          <IconButton sx={{ width: '32px', height: '32px' }}>
            <img src={mobileDarkIcon} alt='phone icon' width={'16px'} height={'16px'} />
          </IconButton>
          <IconButton
            sx={{ width: '32px', height: '32px' }}
            onClick={() => {
              if (handleEditDrawer) {
                handleEditDrawer('Edit Contact');
              }
            }}
          >
            <img src={editIcon} alt='edit icon' width={'20px'} height={'20px'} />
          </IconButton>
          <IconButton
            sx={{ width: '32px', height: '32px' }}
            onClick={() => modalDispatch({ type: 'OPEN', payload: 'delete' })}
          >
            <img src={deleteIcon} alt='delete icon' width={'20px'} height={'20px'} />
          </IconButton>
        </Box>
      </Box>
      <Divider
        component='div'
        sx={{
          borderColor: zeplinColor.Neutral5,
          position: 'sticky',
          top: { xs: '84px', xl: '104px' },
        }}
      />
      <Box sx={{ padding: { xs: '20px', xl: '24px' } }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: { xs: '20px', xl: '24px' } }}>
          <Box sx={{ display: 'flex', gap: '48px' }}>
            <Box sx={{ display: 'flex', gap: '8px', flex: 1, alignItems: 'center' }}>
              <img
                src={accountNameIcon}
                className={styles.icon}
                style={{ objectFit: 'fill' }}
                alt='account name icon'
              />
              <Typography variant='responsiveBody2' color={zeplinColor.Neutral8} sx={{ flex: 1 }}>
                Account name
              </Typography>
              <Typography variant='responsiveBody2' sx={{ flex: 3 }}>
                {selected?.accountName}
              </Typography>
            </Box>
          </Box>
          <Box sx={{ display: 'flex', gap: '48px' }}>
            <Box sx={{ display: 'flex', gap: '8px', flex: 1, alignItems: 'center' }}>
              <img
                src={companyIcon}
                className={styles.icon}
                style={{ objectFit: 'fill' }}
                alt='account name icon'
              />
              <Typography variant='responsiveBody2' color={zeplinColor.Neutral8} sx={{ flex: 1 }}>
                Company Name
              </Typography>
              <Typography variant='responsiveBody2' sx={{ flex: 3 }}>
                {selected?.company}
              </Typography>
            </Box>
          </Box>
          <Box sx={{ display: 'flex', gap: '48px' }}>
            <Box sx={{ display: 'flex', gap: '8px', flex: 1, alignItems: 'center' }}>
              <img
                src={mobileIcon}
                className={styles.icon}
                style={{ objectFit: 'fill' }}
                alt='phone icon'
              />
              <Typography variant='responsiveBody2' color={zeplinColor.Neutral8} sx={{ flex: 1 }}>
                Mobile
              </Typography>
              <Typography sx={{ flex: 3 }}>
                {selected?.phone ? <CopyableText textToCopy={selected?.phone} /> : 'NA'}
              </Typography>
            </Box>
          </Box>
          <Box sx={{ display: 'flex', gap: '48px' }}>
            <Box sx={{ display: 'flex', gap: '8px', flex: 1, alignItems: 'center' }}>
              <img
                src={emailIcon}
                className={styles.icon}
                style={{ objectFit: 'fill' }}
                alt='email icon'
              />
              <Typography variant='responsiveBody2' color={zeplinColor.Neutral8} sx={{ flex: 1 }}>
                Email
              </Typography>

              <Typography variant='responsiveBody2' sx={{ flex: 3 }} color={zeplinColor.Brand1}>
                {selected?.email ? <CopyableText textToCopy={selected?.email} /> : 'NA'}
              </Typography>
            </Box>
          </Box>
          <Box sx={{ display: 'flex', gap: '48px' }}>
            <Box sx={{ display: 'flex', gap: '8px', flex: 1, alignItems: 'center' }}>
              <img
                src={relationshipIcon}
                className={styles.icon}
                style={{ objectFit: 'fill' }}
                alt='relationship icon'
              />
              <Typography variant='responsiveBody2' color={zeplinColor.Neutral8} sx={{ flex: 1 }}>
                Relationship
              </Typography>

              <Box sx={{ flex: 3 }}>
                <Chip
                  label={
                    <Typography variant='responsiveLabel1' color={zeplinColor.Neutral1}>
                      {selected?.roles ? selected?.roles : 'NA'}
                    </Typography>
                  }
                  sx={{
                    backgroundColor: zeplinColor.BasicBlack,
                    padding: '5px 12px 5px 12px',
                    borderRadius: '50px',
                    height: '24px',
                  }}
                />
              </Box>
            </Box>
          </Box>
          <Box sx={{ display: 'flex', gap: '48px' }}>
            <Box sx={{ display: 'flex', gap: '8px', flex: 1, alignItems: 'center' }}>
              <img
                src={birthDateIcon}
                className={styles.icon}
                style={{ objectFit: 'fill' }}
                alt='birthdate icon'
              />
              <Typography variant='responsiveBody2' color={zeplinColor.Neutral8} sx={{ flex: 1 }}>
                Birthdate
              </Typography>

              <Typography variant='responsiveBody2' sx={{ flex: 3 }}>
                {selected?.birthdate ? Utilities.formatDate(selected.birthdate) : 'NA'}
              </Typography>
            </Box>
          </Box>
        </Box>
      </Box>
      <CustomDeleteModal
        isModalOpen={modalState.delete}
        modalDispatch={modalDispatch}
        onDelete={handleDeleteContact}
        title='Delete Contact?'
        itemName={selected?.name}
      />
    </CustomCard>
  );
};
